import { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import CustomBreadCrumbs from "../../common/breadcrumbs";
import UserFilters from "../users/filters";
import { Dropdown } from "primereact/dropdown";
// Imported from the leaf modules, not the constants barrel: the barrel
// loads this screen through MenuConstants, and SET_QUERY_BY_REPORT reads
// GlobalConstants at module scope (see constants/ImportOrder.test.js).
import * as GlobalConstants from "../../../constants/GlobalConstants";
import * as SavedReportConstants from "../../../constants/SavedReportConstants";
import LearningObjectReport from "./LearningObjectReport";
import LearningProgramReport from "./LearningProgramReport";
import LearningMilestoneReport from "./LearningMilestoneReport";
import { TextInput } from "../../common/fields";
import { MultiSelect } from "primereact/multiselect";
import SavedReportActions from "../savedReports/SavedReportActions";
import useOpenSavedReport from "../savedReports/useOpenSavedReport";
import {
  fromReportLevel,
  fromSavedFilters,
  toReportLevel,
  toSavedFilters,
} from "../../../helpers/SavedReportFilters";
import {
  savedReportCreateRequest,
  savedReportDetailsRequest,
  savedReportUpdateRequest,
} from "../../../store/slices/savedReportSlice";
import {
  setLearningMilestoneQuery,
  setLearningObjectQuery,
  setLearningProgramQuery,
} from "../../../store/slices/reportSlice";

const noop = () => {};

export const Report = ({
  labels,
  savedReport = null,
  savedReportAction = null,
  savedReportRequesting = false,
  savedReportError = null,
  reportQueries = {},
  loadSavedReport = noop,
  createSavedReport = noop,
  updateSavedReport = noop,
  setReportQuery = noop,
}) => {
  const navigate = useNavigate();
  const intl = useIntl();
  // The searches start empty here as well as in their inputs, so the search
  // debounces below have nothing to copy when the screen mounts.
  const [additionalQuery, setAdditionalQuery] = useState({
    type: GlobalConstants.TRAINING_TYPE_REQUIRED,
    namePattern: "",
    objectNamePattern: "",
  });
  const [filters, setFilters] = useState([]);
  const [namePattern, setNamePattern] = useState("");
  const [objectNamePattern, setObjectNamePattern] = useState("");
  const reportOption = (id) => ({
    ...GlobalConstants.REPORTS.find((item) => item.id === id),
    label: intl.formatMessage({ id }),
  });
  const [currentReport, setCurrentReport] = useState(
    reportOption(GlobalConstants.REPORTS[0].id)
  );

  // One stable object per filter change, so the report widgets only fetch
  // again when a filter changes (not when the saved report state changes).
  const reportQuery = useMemo(
    () => ({
      ...additionalQuery,
      status: additionalQuery?.status?.join(",") || "",
    }),
    [additionalQuery]
  );

  // Search, status and training type are shared filters: every level fetches
  // against them, so changing one has to reset every level's own stored
  // page, not just the level in view. Otherwise switching level afterwards
  // can land on a page that is now past the end of its filtered list.
  // additionalQuery itself must never carry a pageNo: the level reports
  // build the user-list request as { ...store query, ...additionalQuery },
  // so a pageNo here would permanently override the store's own paging
  // (see Report.test.js).
  //
  // The search debounces call this from a timer: it reads the store queries
  // of the latest render, not of the render that scheduled the timer, or a
  // sort (or page size) stored in between would be put back (B-BUG-2).
  const latestReportQueries = useRef(reportQueries);
  latestReportQueries.current = reportQueries;
  const resetAllLevelPages = () => {
    GlobalConstants.REPORTS.forEach((item) => {
      setReportQuery(item.id, {
        ...latestReportQueries.current[item.id],
        pageNo: 1,
      });
    });
  };

  const applySavedReport = (report) => {
    const saved = fromSavedFilters(report.filters);
    const reportId = fromReportLevel(report.level);
    setCurrentReport(reportOption(reportId));
    setNamePattern(saved.namePattern);
    setObjectNamePattern(saved.objectNamePattern);
    setFilters(saved.userFilters);
    setAdditionalQuery({
      type: saved.type,
      status: saved.statuses,
      namePattern: saved.namePattern,
      objectNamePattern: saved.objectNamePattern,
    });
    // Status, type and search are shared filters a saved report rewrites for
    // every level, so every level's stored page resets here too, not just
    // the level being applied, the same way a live filter change does.
    resetAllLevelPages();
    setReportQuery(reportId, {
      ...reportQueries[reportId],
      sortColumn: saved.sortColumn,
      sortBy: saved.sortBy,
      pageNo: 1,
    });
  };

  // Set by handleCreateSavedReport just before dispatching a create, so the
  // hook only redirects to a create this mounted screen actually started
  // (not a settled "create" left over in the persisted store from an
  // earlier visit).
  const createdReportRef = useRef(false);

  const openSavedReport = useOpenSavedReport({
    kind: SavedReportConstants.SAVED_REPORT_KIND_ADMIN,
    savedReport,
    savedReportAction,
    savedReportRequesting,
    savedReportError,
    loadSavedReport,
    createdRef: createdReportRef,
    onApply: applySavedReport,
  });

  const handleCreateSavedReport = (body) => {
    createdReportRef.current = true;
    createSavedReport(body);
  };

  const buildSavedReport = (name) => ({
    name,
    kind: SavedReportConstants.SAVED_REPORT_KIND_ADMIN,
    level: toReportLevel(currentReport.id),
    filters: toSavedFilters({
      namePattern,
      objectNamePattern,
      status: additionalQuery.status,
      type: additionalQuery.type,
      sortColumn: reportQueries[currentReport.id]?.sortColumn,
      sortBy: reportQueries[currentReport.id]?.sortBy,
      userFilters: filters,
    }),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (additionalQuery?.namePattern !== namePattern) {
        // Only this search changes: the other filters come from the
        // latest state, not from the render this timer was scheduled in.
        setAdditionalQuery((current) => ({
          ...current,
          namePattern,
        }));
        resetAllLevelPages();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [namePattern]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (additionalQuery?.objectNamePattern !== objectNamePattern) {
        // Only this search changes: the other filters come from the
        // latest state, not from the render this timer was scheduled in.
        setAdditionalQuery((current) => ({
          ...current,
          objectNamePattern,
        }));
        resetAllLevelPages();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [objectNamePattern]);

  const getSearchPlaceholder = () => {
    if (currentReport.id === GlobalConstants.LEARNING_OBJECT_REPORTS) {
      return intl.formatMessage({ id: "search_learning_objects" });
    } else if (currentReport.id === GlobalConstants.LEARNING_PROGRAM_REPORTS) {
      return intl.formatMessage({ id: "search_learning_programs" });
    } else if (
      currentReport.id === GlobalConstants.LEARNING_MILESTONE_REPORTS
    ) {
      return intl.formatMessage({ id: "search_learning_milestones" });
    }
  };

  return (
    <div>
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title flex-column align-items-start">
            {intl.formatMessage({ id: "reports" })}
            {(openSavedReport && (
              <div className="text-base font-normal">
                {intl.formatMessage(
                  { id: "saved_report_open_title" },
                  { name: openSavedReport.name }
                )}
              </div>
            )) ||
              null}
          </div>
          <SavedReportActions
            openSavedReport={openSavedReport}
            requesting={savedReportRequesting}
            buildSavedReport={buildSavedReport}
            createSavedReport={handleCreateSavedReport}
            updateSavedReport={updateSavedReport}
          />
        </div>
        <div
          className="edexlms-page-body"
          style={{
            height: "calc(100vh - 64px - 20px - 45px - 40px - 14px - 40px)",
            overflowY: "scroll",
          }}
        >
          <div className="edexlms-search-filter-bar">
            <Dropdown
              style={{ width: "250px" }}
              value={currentReport}
              options={GlobalConstants.REPORTS.map((item) => ({
                ...item,
                label: intl.formatMessage({ id: item.id }),
              }))}
              onChange={(e) => setCurrentReport(e.value)}
              data-testid="report-select"
              placeholder={intl.formatMessage({ id: "select_report" })}
            />
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({ id: "search_users" })}
              input={{
                name: "search_name",
                value: namePattern,
                onChange: (e) => setNamePattern(e.target.value),
              }}
            />
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={getSearchPlaceholder()}
              input={{
                name: "search_object",
                value: objectNamePattern,
                onChange: (e) => setObjectNamePattern(e.target.value),
              }}
            />
            <MultiSelect
              value={additionalQuery.status}
              style={{ width: "200px" }}
              options={GlobalConstants.LEARNING_STATUSES.map((item) => ({
                ...item,
                label: intl.formatMessage({ id: item.id.toLocaleLowerCase() }),
              }))}
              showClear={true}
              onChange={(e) => {
                setAdditionalQuery({
                  ...additionalQuery,
                  status: e.value,
                });
                resetAllLevelPages();
              }}
              data-testid="report-status"
              placeholder={intl.formatMessage({ id: "select_status" })}
            />
            <Dropdown
              value={additionalQuery.type}
              style={{ width: "200px" }}
              options={GlobalConstants.REPORT_TRAINING_TYPES.map((item) => ({
                ...item,
                label: intl.formatMessage({ id: item.id }),
              }))}
              onChange={(e) => {
                setAdditionalQuery({
                  ...additionalQuery,
                  type: e.value,
                });
                // Reset paging through the store, the same way
                // applySavedReport does, instead of additionalQuery: a
                // pageNo on additionalQuery would permanently override the
                // store's own paging (see Report.test.js). Training type is
                // a shared filter, so every level's page resets here too.
                resetAllLevelPages();
              }}
              data-testid="report-training-type"
              placeholder={intl.formatMessage({ id: "training_type" })}
            />
            <UserFilters filters={filters} onFilterChange={setFilters} />
          </div>
          {(currentReport.id === GlobalConstants.LEARNING_OBJECT_REPORTS && (
            <LearningObjectReport
              additionalQuery={reportQuery}
              filters={filters}
            />
          )) ||
            null}
          {(currentReport.id === GlobalConstants.LEARNING_PROGRAM_REPORTS && (
            <LearningProgramReport
              additionalQuery={reportQuery}
              filters={filters}
            />
          )) ||
            null}
          {(currentReport.id === GlobalConstants.LEARNING_MILESTONE_REPORTS && (
            <LearningMilestoneReport
              additionalQuery={reportQuery}
              filters={filters}
            />
          )) ||
            null}
        </div>
      </div>
    </div>
  );
};

const selectReportQueries = createSelector(
  [
    (state) => state.reports.learningPrograms.query,
    (state) => state.reports.learningMilestones.query,
    (state) => state.reports.learningObjects.query,
  ],
  (programs, milestones, objects) => ({
    [GlobalConstants.LEARNING_PROGRAM_REPORTS]: programs,
    [GlobalConstants.LEARNING_MILESTONE_REPORTS]: milestones,
    [GlobalConstants.LEARNING_OBJECT_REPORTS]: objects,
  })
);

const SET_QUERY_BY_REPORT = {
  [GlobalConstants.LEARNING_PROGRAM_REPORTS]: setLearningProgramQuery,
  [GlobalConstants.LEARNING_MILESTONE_REPORTS]: setLearningMilestoneQuery,
  [GlobalConstants.LEARNING_OBJECT_REPORTS]: setLearningObjectQuery,
};

const mapStateToProps = (state) => ({
  savedReport: state.savedReports.record,
  savedReportAction: state.savedReports.action,
  savedReportRequesting: state.savedReports.requesting,
  savedReportError: state.savedReports.error,
  reportQueries: selectReportQueries(state),
});

const mapDispatchToProps = (dispatch) => ({
  loadSavedReport: (data) => dispatch(savedReportDetailsRequest(data)),
  createSavedReport: (data) => dispatch(savedReportCreateRequest(data)),
  updateSavedReport: (data) => dispatch(savedReportUpdateRequest(data)),
  setReportQuery: (reportId, query) =>
    dispatch(SET_QUERY_BY_REPORT[reportId](query)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Report);
